import { useMutation, useQuery } from '@tanstack/react-query';
import { FlaskConical, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { cn } from '../lib/utils';

type SandboxStatus = { enabled: boolean };

/**
 * Production-only affordance: opens signed sandbox URL in a new tab (requires Backend SANDBOX_* env).
 * Hidden when sandbox is not configured.
 */
export default function SandboxTryoutButton({ className = '' }: { className?: string }) {
  const { data, isError } = useQuery({
    queryKey: ['sandbox-status'],
    queryFn: async () => {
      const { data: body } = await api.get<SandboxStatus>('/sandbox/status');
      return body;
    },
    staleTime: 60_000,
  });

  const launch = useMutation({
    mutationFn: async () => {
      const { data: body } = await api.post<{ url: string }>('/sandbox/launch');
      return body.url;
    },
    onSuccess: (url) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    },
  });

  if (isError || !data?.enabled) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => launch.mutate()}
      disabled={launch.isPending}
      className={cn(
        'inline-flex items-center justify-center shrink-0 h-9 w-9 rounded-lg p-0 text-glass-text-2 hover:text-glass-text hover:bg-glass-3 disabled:opacity-60',
        'md:h-auto md:w-auto md:gap-1.5 md:px-2.5 md:py-1.5 md:border md:border-cyan-500/40 md:bg-cyan-500/10 md:text-xs md:font-medium md:text-cyan-700 md:dark:text-cyan-200 md:dark:border-cyan-400/35 md:hover:bg-cyan-500/15',
        className
      )}
      title="Opens the sandbox site in a new tab and tryout demo accounts!"
      aria-label="Tryout in sandbox"
    >
      {launch.isPending ? (
        <Loader2 className="h-5 w-5 shrink-0 animate-spin md:h-3.5 md:w-3.5" aria-hidden />
      ) : (
        <FlaskConical className="h-5 w-5 shrink-0 md:h-3.5 md:w-3.5" aria-hidden />
      )}
      <span className="hidden md:inline">Tryout in sandbox</span>
    </button>
  );
}
